import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
export const useTrainingTracking = () => {
  const { getAuthHeaders } = useAuth();
  const [groups, setGroups] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orphaned, setOrphaned] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Filter/search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form state
  const [form, setForm] = useState({
    employee_id: "",
    certificate_name: "",
    training_date: "",
    validity_years: 0,
    validity_months: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      const [recordsRes, employeesRes, typesRes, orphanedRes] = await Promise.all([
        apiClient.get(`/training/records`),
        apiClient.get(`/employees`),
        apiClient.get(`/training/certificate-types`),
        apiClient.get(`/training/records/orphaned`),
      ]);
      setGroups(recordsRes.data.groups || []);
      setTotalRecords(recordsRes.data.total_records || 0);
      setEmployees(employeesRes.data.filter(e => e.is_active));
      setCertTypes(typesRes.data || []);
      setOrphaned(orphanedRes.data.orphaned || []);
    } catch {
      toast.error("Failed to load training data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setForm({
      employee_id: "",
      certificate_name: "",
      training_date: "",
      validity_years: 0,
      validity_months: 0,
    });
  };

  const openAdd = () => {
    resetForm();
    setAddOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      employee_id: record.employee_id,
      certificate_name: record.certificate_name,
      training_date: record.training_date,
      validity_years: record.validity_years || 0,
      validity_months: record.validity_months || 0,
    });
    setEditOpen(true);
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.name : "Unknown";
  };

  const handleAdd = async () => {
    if (!form.employee_id || !form.certificate_name || !form.training_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.validity_years === 0 && form.validity_months === 0) {
      toast.error("Please enter a validity period");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        employee_name: getEmployeeName(form.employee_id),
      };
      const res = await apiClient.post(`/training/records`, payload);
      setGroups(res.data.groups || []);
      setTotalRecords(res.data.total_records || 0);
      setAddOpen(false);
      resetForm();
      toast.success("Training record added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add record");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editingRecord) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        employee_name: getEmployeeName(form.employee_id),
      };
      const res = await apiClient.put(
        `/training/records/${editingRecord.id}`,
        payload
      );
      setGroups(res.data.groups || []);
      setTotalRecords(res.data.total_records || 0);
      setEditOpen(false);
      setEditingRecord(null);
      resetForm();
      toast.success("Training record updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update record");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      const res = await apiClient.delete(`/training/records/${recordId}`);
      setGroups(res.data.groups || []);
      setTotalRecords(res.data.total_records || 0);
      toast.success("Training record deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete record");
    }
  };

  const handleDeleteOrphan = async (recordId) => {
    try {
      const res = await apiClient.delete(`/training/records/${recordId}`);
      setGroups(res.data.groups || []);
      setTotalRecords(res.data.total_records || 0);
      setOrphaned((prev) => prev.filter((r) => r.id !== recordId));
      toast.success("Orphaned record removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete record");
    }
  };

  // Build a Set of active employee ids so records belonging to deactivated
  // employees can be hidden from the tracking views and stats.
  const activeEmployeeIds = new Set(employees.map((e) => e.id));

  // Filter groups
  const filteredGroups = groups
    .map((group) => {
      // Hide records belonging to inactive employees
      let records = group.records.filter((r) =>
        activeEmployeeIds.has(r.employee_id)
      );

      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        records = records.filter(
          (r) =>
            r.employee_name.toLowerCase().includes(term) ||
            r.certificate_name.toLowerCase().includes(term)
        );
      }

      // Status filter
      if (statusFilter !== "all") {
        records = records.filter((r) => r.status === statusFilter);
      }

      return { ...group, records };
    })
    .filter((group) => group.records.length > 0);

  // Stats — only count records for active employees so the totals match
  // what's visible on the page.
  const visibleRecords = groups.flatMap((g) =>
    g.records.filter((r) => activeEmployeeIds.has(r.employee_id))
  );
  const stats = {
    total: visibleRecords.length,
    valid: visibleRecords.filter((r) => r.status === "valid").length,
    expiring: visibleRecords.filter((r) => r.status === "expiring_soon").length,
    expired: visibleRecords.filter((r) => r.status === "expired").length,
  };

  return {
    groups: filteredGroups, totalRecords, employees, certTypes, loading, saving, stats,
    orphaned,
    addOpen, editOpen, editingRecord,
    searchTerm, statusFilter, form,
    setAddOpen, setEditOpen, setSearchTerm, setStatusFilter, setForm,
    openAdd, openEdit, handleAdd, handleEdit, handleDelete, handleDeleteOrphan,
  };
};
